// src/cn/pages/Charting/ChartingPageCN.jsx
import { useEffect, useState } from "react";
import { computeChartText, parsePreviousEMR, labelForDay, labelForStage, SIKTAM_DAY_OPTIONS, SIKTAM_STAGE_MIN, SIKTAM_STAGE_MAX } from "../../../lib/chartingApi";

function getLang() {
  if (typeof document !== "undefined" && document.documentElement?.lang) {
    return document.documentElement.lang;
  }
  return "ko";
}

export default function ChartingPageCN() {
  const lang = getLang();

  // 1. 이전 EMR
  const [prevEmr, setPrevEmr] = useState("");

  // 2. 이번 처방 (동적 식사탕 + 고정 항목들)
  const [siktamRows, setSiktamRows] = useState(() => [{ id: String(Date.now()), stage: 1, days: 15 }]);
  const [bulkDays, setBulkDays] = useState("0");
  const [soomyunDays, setSoomyunDays] = useState("0");
  const [baetsalDose, setBaetsalDose] = useState("0");
  const [baetsalDays, setBaetsalDays] = useState("0");
  const [doksokillDays, setDoksokillDays] = useState("0");

  // 토글: 곤약/책자/뱃+/독+
  const [isGonyak, setIsGonyak] = useState(false);
  const [isChaekja, setIsChaekja] = useState(false);
  const [isBaetsalPlus, setIsBaetsalPlus] = useState(false);
  const [isDoksokillPlus, setIsDoksokillPlus] = useState(false);

  // 3. 결과
  const [newEmrText, setNewEmrText] = useState("");

  // 경고 모달
  const [warnings, setWarnings] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const parsed = parsePreviousEMR(prevEmr);
    if (parsed.lastSiktamStage && siktamRows.length > 0) {
      setSiktamRows((rows) => {
        const [head, ...rest] = rows;
        return [{ ...head, stage: parsed.lastSiktamStage }, ...rest];
      });
    }
    recompute(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevEmr]);

  useEffect(() => {
    recompute(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siktamRows, soomyunDays, baetsalDose, baetsalDays, doksokillDays, isGonyak, isChaekja, isBaetsalPlus, isDoksokillPlus]);

  // 일괄 적용
  useEffect(() => {
    if (bulkDays === "0") return;
    // 수/뱃/독
    setSoomyunDays(bulkDays);
    setBaetsalDays(bulkDays);
    setDoksokillDays(bulkDays);
    setSiktamRows((rows) =>
      rows.map((r) => {
        const d = Number(bulkDays);
        return SIKTAM_DAY_OPTIONS.includes(d) ? { ...r, days: d } : r;
      })
    );
    // 즉시 반영
    recompute(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkDays]);

  function recompute(shouldValidate) {
    const { text, warnings } = computeChartText({
      prevEmr,
      siktam: siktamRows.map((r) => ({ stage: r.stage, days: r.days })),
      soomyunDays: Number(soomyunDays),
      baetsalDose: Number(baetsalDose),
      baetsalDays: Number(baetsalDays),
      doksokillDays: Number(doksokillDays),
      toggles: {
        gonyak: isGonyak,
        chaekja: isChaekja,
        baetsalPlus: isBaetsalPlus,
        doksokillPlus: isDoksokillPlus,
      },
      validate: !!shouldValidate,
    });
    setNewEmrText(text);
    if (shouldValidate && warnings.length > 0) {
      setWarnings(warnings);
      setModalOpen(true);
    } else {
      setWarnings([]);
      setModalOpen(false);
    }
  }

  function handleAddSiktam() {
    setSiktamRows((rows) => {
      let nextStage = 1;
      let nextDays = 15;
      if (rows.length > 0) {
        const last = rows[rows.length - 1];
        const lastStage = Number(last.stage) || 1;
        const lastDays = Number(last.days) || 15;
        nextStage = Math.min(SIKTAM_STAGE_MAX, lastStage + 1);
        const idx = SIKTAM_DAY_OPTIONS.indexOf(lastDays);
        nextDays = idx >= 0 ? SIKTAM_DAY_OPTIONS[Math.min(idx + 1, SIKTAM_DAY_OPTIONS.length - 1)] : 15;
      }
      return [...rows, { id: String(Date.now()), stage: nextStage, days: nextDays }];
    });
  }

  function handleRemoveSiktam(id) {
    setSiktamRows((rows) => rows.filter((r) => r.id !== id));
  }

  // 복사
  async function handleCopy() {
    if (!newEmrText) return;
    try {
      await navigator.clipboard.writeText(newEmrText);
      alert(lang === "zh-CN" ? "已复制!" : "복사 완료!");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = newEmrText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      alert(lang === "zh-CN" ? "已复制!" : "복사 완료!");
    }
  }

  // 뷰
  return (
    <section className="charting">
      <div id="content_title">
        <h2>（仅限食事汤）复诊患者 EMR 记录助手 ver.1.0</h2>
        <p>请输入既往记录与本次处方，生成新的 EMR 记录内容。</p>
      </div>

      <div className="content">
        <div className="">
          {/* 좌측: 입력 섹션 */}
          <div>
            <div className="mrb30">
              <h3 className="sectionTitle">1. 粘贴既往 EMR 记录</h3>
              <textarea placeholder={`示例)\n${process.env.REACT_APP_CN_SIKTAM_SAYAK} 7-15 / ${process.env.REACT_APP_CN_SUMYEON_KILL}15 / #${process.env.REACT_APP_CN_JIBANG_KILL}45\n\nss 15d\n15/90\n\nsk 15d\n15/90\n\nzk 45d\n45/270`}
                        value={prevEmr}
                        onChange={(e) => setPrevEmr(e.target.value)} />
            </div>

            <div className="mrb30">
              <h3 className="sectionTitle">2. 选择本次处方剩余用药</h3>

              <div className="chartiong_grid">
                {/* 일괄 적용 */}
                <div className="batch">
                  <div className="charting_sel_tit">批量应用</div>
                  <div className="selectarea">
                    <select value={bulkDays} onChange={(e) => setBulkDays(e.target.value)}>
                      <option value="0">不批量选择</option>
                      <option value="15">全部 15 天</option>
                      <option value="30">全部 30 天</option>
                      <option value="45">全部 45 天</option>
                    </select>
                  </div>
                </div>

                {/* 추가 항목 */}
                <div className="plus">
                  <div className="charting_sel_tit">附加项目</div>
                  <div className="selectarea">
                    <button type="button" className={` ${isGonyak ? "toggled" : ""}`} onClick={() => setIsGonyak((v) => !v)}>
                      魔芋
                    </button>
                    <button type="button" className={` ${isChaekja ? "toggled" : ""}`} onClick={() => setIsChaekja((v) => !v)}>
                      手册
                    </button>
                  </div>
                </div>

                {/* 식사탕 (동적 행) */}
                <div className="food">
                  <div>
                    <div className="charting_sel_tit">食事汤</div>
                    <button type="button" onClick={handleAddSiktam} className="addBtn">
                      +
                    </button>
                  </div>
                  <div>
                    {siktamRows.map((row) => (
                      <div key={row.id} className="selectarea">
                        {/* 단계 */}
                        <select value={row.stage} onChange={(e) => setSiktamRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, stage: Number(e.target.value) } : r)))}>
                          {Array.from({ length: SIKTAM_STAGE_MAX }, (_, i) => i + SIKTAM_STAGE_MIN).map((n) => (
                            <option key={n} value={n}>
                              {labelForStage(n)}
                            </option>
                          ))}
                        </select>

                        {/* 일수 */}
                        <select value={row.days} onChange={(e) => setSiktamRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, days: Number(e.target.value) } : r)))}>
                          {SIKTAM_DAY_OPTIONS.map((d) => (
                            <option key={d} value={d}>
                              {labelForDay(d)}
                            </option>
                          ))}
                        </select>

                        {/* 제거 */}
                        <button type="button" onClick={() => handleRemoveSiktam(row.id)}>
                          -
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 수면킬 */}
                <div className="sleep">
                  <div className="charting_sel_tit">睡眠KILL</div>
                  <div className="selectarea">
                    <select value={soomyunDays} onChange={(e) => setSoomyunDays(e.target.value)}>
                      <option value="0">0 天</option>
                      <option value="15">15 天</option>
                      <option value="30">30 天</option>
                      <option value="45">45 天</option>
                    </select>
                  </div>
                </div>

                {/* 뱃살킬 */}
                <div className="belly">
                  <div>
                    <div className="charting_sel_tit">腹部脂肪KILL</div>
                    <button type="button" className={`addBtn mini ${isBaetsalPlus ? "toggled" : ""}`} onClick={() => setIsBaetsalPlus((v) => !v)}>
                      +
                    </button>
                  </div>

                  <div className="selectarea">
                    <select value={baetsalDose} onChange={(e) => setBaetsalDose(e.target.value)}>
                      <option value="0">无次数</option>
                      <option value="1">1 次/日</option>
                      <option value="2">2 次/日</option>
                      <option value="3">3 次/日</option>
                      <option value="4">4 次/日</option>
                      <option value="5">5 次/日</option>
                    </select>

                    <select value={baetsalDays} onChange={(e) => setBaetsalDays(e.target.value)}>
                      <option value="0">0 天</option>
                      <option value="15">15 天</option>
                      <option value="30">30 天</option>
                      <option value="45">45 天</option>
                    </select>
                  </div>
                </div>

                {/* 독소킬 */}
                <div className="poison">
                  <div>
                    <div className="charting_sel_tit">排毒KILL</div>
                    <button type="button" className={`addBtn mini ${isDoksokillPlus ? "toggled" : ""}`} onClick={() => setIsDoksokillPlus((v) => !v)}>
                      +
                    </button>
                  </div>

                  <div className="selectarea">
                    <select value={doksokillDays} onChange={(e) => setDoksokillDays(e.target.value)}>
                      <option value="0">0 天</option>
                      <option value="15">15 天</option>
                      <option value="30">30 天</option>
                      <option value="45">45 天</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 우측: 결과 섹션 */}
          <div>
            <div>
              <div lassName="mrb30">
                <h3 className="sectionTitle">3. 本次 EMR 记录内容</h3>
                <textarea readOnly value={newEmrText} rows={10} />
                <button className="submitbtn" onClick={() => recompute(true)}>
                  {lang === "zh-CN" ? "生成并检查错误" : "생성 및 오류 확인"}
                </button>
                <button className="copy" onClick={handleCopy}>
                  {lang === "zh-CN" ? "复制" : "복사"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 경고 모달 */}
      {modalOpen && (
        <div
          className="modal-backdrop"
          onClick={(e) => {
            if (e.target.classList.contains("modal-backdrop")) setModalOpen(false);
          }}
        >
          <div className="modal">
            <h3 className="modal-title">{lang === "zh-CN" ? "处方错误警告" : "처방 오류 경고"}</h3>
            <div className="modal">
              {lang === "zh-CN" ? "以下项目超过了可处方总量：" : "다음 항목에서 처방 가능 총량을 초과했습니다:"}
              <ul className="modal-list">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
            <button className=" danger wide" onClick={() => setModalOpen(false)}>
              {lang === "zh-CN" ? "确认" : "확인"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
